import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../services/api';
import { useIsAuthorized, useUserInfo } from '../contexts/GlobalStateContext';
import { toast } from 'react-toastify';

// ─────────────────────────────────────────────────────────────────────────────
// CRUD-sida för Verksamhetsområden (SystemAdmin)
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_FORM = { id: 0, name: '', stationer: [] };

const Vo = () => {
  const { isAuthorized } = useIsAuthorized();
  const { userInfo } = useUserInfo();
  const isSystemAdmin = userInfo?.role === 'SystemAdmin';

  const [vos, setVos] = useState([]);
  const [allStationer, setAllStationer] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Ladda alla stationer ──────────────────────────────────────────────────
  useEffect(() => {
    fetchWithAuth('/api/reports/allastationer')
      .then(r => r.json())
      .then(d => setAllStationer(d.filter(s => s !== '---').sort()))
      .catch(() => {});
  }, []);

  // ── Ladda verksamhetsområden ──────────────────────────────────────────────
  const loadVos = () =>
    fetchWithAuth('/api/verksamhetsomraden')
      .then(r => r.json())
      .then(setVos)
      .catch(() => toast.error('Kunde inte ladda verksamhetsområden'));

  useEffect(() => { loadVos(); }, []);

  // ── Öppna formulär ────────────────────────────────────────────────────────
  const openCreate = () => {
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (vo) => {
    setForm({ id: vo.id, name: vo.name, stationer: [...vo.stationer] });
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setForm(EMPTY_FORM); };

  // ── Spara (skapa / uppdatera) ─────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim()) { toast.warn('Ange ett namn'); return; }
    setSaving(true);
    try {
      const isEdit = form.id > 0;
      const url    = isEdit ? `/api/verksamhetsomraden/${form.id}` : '/api/verksamhetsomraden';
      const method = isEdit ? 'PUT' : 'POST';
      const res    = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name.trim(), stationer: form.stationer }),
      });
      if (!res.ok) { toast.error('Sparning misslyckades'); return; }
      toast.success(isEdit ? 'Uppdaterat' : 'Skapat');
      closeForm();
      loadVos();
    } finally { setSaving(false); }
  };

  // ── Ta bort ───────────────────────────────────────────────────────────────
  const handleDelete = async (vo) => {
    if (!window.confirm(`Ta bort "${vo.name}"?`)) return;
    const res = await fetchWithAuth(`/api/verksamhetsomraden/${vo.id}`, { method: 'DELETE' });
    if (res.ok) { toast.success('Borttaget'); loadVos(); }
    else toast.error('Borttagning misslyckades');
  };

  // ── Stationskryssrutorna i formuläret ─────────────────────────────────────
  const toggleStation = (s) =>
    setForm(f => ({
      ...f,
      stationer: f.stationer.includes(s)
        ? f.stationer.filter(x => x !== s)
        : [...f.stationer, s],
    }));

  if (!isAuthorized || !isSystemAdmin) return null;

  const btnBase = 'tw-text-xs tw-font-bold tw-px-3 tw-py-1 tw-rounded tw-transition-colors';

  return (
    <div className="tw-px-4 sm:tw-px-6 lg:tw-px-8">
      <div className="tw-max-w-5xl tw-mx-auto tw-py-3">
        <div className="tw-rounded-3xl tw-overflow-hidden tw-border tw-border-blue-500">

          {/* ── Rubrik ──────────────────────────────────────────────────── */}
          <div className="tw-bg-gradient-to-r tw-from-blue-600 tw-to-blue-700 tw-text-white tw-px-4 tw-py-3 tw-flex tw-items-center tw-justify-between">
            <h3 className="tw-m-0 tw-text-2xl tw-font-bold">Verksamhetsområden</h3>
            <button
              onClick={openCreate}
              className="tw-bg-white tw-text-blue-700 tw-font-bold tw-text-sm tw-px-4 tw-py-1.5 tw-rounded hover:tw-bg-blue-50"
            >
              + Nytt verksamhetsområde
            </button>
          </div>

          <div className="tw-p-6">

            {/* ── Formulär (skapa / redigera) ──────────────────────────── */}
            {showForm && (
              <div className="tw-mb-6 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-xl tw-p-5">
                <h4 className="tw-text-sm tw-font-bold tw-text-blue-800 tw-mb-4">
                  {form.id > 0 ? 'Redigera verksamhetsområde' : 'Nytt verksamhetsområde'}
                </h4>

                {/* Namn */}
                <div className="tw-mb-4">
                  <label className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide tw-block tw-mb-1">
                    Namn
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-1.5 tw-text-sm tw-w-64"
                    placeholder="T.ex. VO Nord"
                  />
                </div>

                {/* Stationer */}
                <div className="tw-mb-4">
                  <label className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide tw-block tw-mb-2">
                    Stationer ({form.stationer.length} valda)
                  </label>
                  <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-3
                                  tw-grid tw-grid-cols-3 md:tw-grid-cols-5 tw-gap-2 tw-max-h-48 tw-overflow-y-auto">
                    {allStationer.map(s => (
                      <label
                        key={s}
                        className={`tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-px-2 tw-py-1 tw-cursor-pointer tw-border tw-text-xs tw-transition-colors ${
                          form.stationer.includes(s)
                            ? 'tw-bg-blue-50 tw-border-blue-300 tw-text-blue-800'
                            : 'tw-bg-gray-50 tw-border-gray-200 tw-text-gray-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={form.stationer.includes(s)}
                          onChange={() => toggleStation(s)}
                          className="tw-w-3 tw-h-3"
                        />
                        <span className="tw-truncate">{s}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Knappar */}
                <div className="tw-flex tw-gap-2">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className={`${btnBase} tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-50`}
                  >
                    {saving ? 'Sparar…' : 'Spara'}
                  </button>
                  <button
                    onClick={closeForm}
                    className={`${btnBase} tw-bg-gray-200 tw-text-gray-700 hover:tw-bg-gray-300`}
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            )}

            {/* ── Lista ───────────────────────────────────────────────── */}
            {vos.length === 0
              ? <p className="tw-text-sm tw-text-gray-400">Inga verksamhetsområden finns ännu.</p>
              : (
                <div className="tw-space-y-3">
                  {vos.map(vo => (
                    <div key={vo.id} className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-p-4">
                      <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                        <div className="tw-flex-1">
                          <p className="tw-font-bold tw-text-sm tw-text-gray-800 tw-mb-1">{vo.name}</p>
                          <p className="tw-text-xs tw-text-gray-500">
                            {vo.stationer.length === 0
                              ? 'Inga stationer kopplade'
                              : vo.stationer.join(', ')}
                          </p>
                        </div>
                        <div className="tw-flex tw-gap-2 tw-flex-shrink-0">
                          <button
                            onClick={() => openEdit(vo)}
                            className={`${btnBase} tw-bg-blue-100 tw-text-blue-700 hover:tw-bg-blue-200`}
                          >
                            Redigera
                          </button>
                          <button
                            onClick={() => handleDelete(vo)}
                            className={`${btnBase} tw-bg-red-100 tw-text-red-700 hover:tw-bg-red-200`}
                          >
                            Ta bort
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Vo;
